// Client-side TRON signing (tronweb, lazily imported so it stays out of the main
// bundle). Builds + signs USDT / TRX txs; the server broadcasts. The private key
// is derived from the seed in-memory and never leaves the device.
import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync } from '@scure/bip39';
import { bytesToHex } from '@noble/hashes/utils';
import { API_BASE } from './api';
import { deriveTron } from './wallet';

export const USDT_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';
const USDT_DECIMALS = 6;
const MAX_UINT256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935';
const PATH = (i: number) => `m/44'/195'/0'/0/${i}`;
const PUBLIC_FULL_HOST = 'https://api.trongrid.io';

const TRC20_APPROVAL_ABI = [
  {
    constant: false,
    stateMutability: 'nonpayable',
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
  {
    constant: true,
    stateMutability: 'view',
    inputs: [
      { name: '_owner', type: 'address' },
      { name: '_spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
];

/** Decimal token amount ("12.5") → integer base-unit string. */
function toUnits(amount: string, decimals = USDT_DECIMALS): string {
  const [whole, frac = ''] = amount.split('.');
  const fracPadded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  return (BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(fracPadded || '0')).toString();
}

/** Derive the user's TRON private key (no 0x) from their mnemonic at `index`. */
export function tronPrivateKey(mnemonic: string, index = 0): string {
  const child = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive(PATH(index));
  if (!child.privateKey) throw new Error('tron_key_derivation_failed');
  return bytesToHex(child.privateKey); // tronweb wants no 0x
}

/** Preload tronweb so the first send doesn't pay its parse cost on the critical path. */
export const preloadTronWeb = () => import('tronweb');

/** Authenticated TronWeb fullHost — API proxies to TronGrid with the server key. */
function tronFullHost(): string {
  const base = API_BASE.replace(/\/$/, '');
  return `${base}/v1/rpc/trx`;
}

function tronAuthHeaders(): Record<string, string> | undefined {
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return undefined;
  return { Authorization: `tma ${initData}` };
}

function isTransientRpcError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /429|503|502|504|rate.?limit|ECONNRESET|Failed to fetch|NetworkError|rpc_unavailable/i.test(
    msg,
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function withBuildRetry<T = any>(fn: () => T | Promise<T>, attempts = 3): Promise<Awaited<T>> {
  let last: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return (await fn()) as Awaited<T>;
    } catch (e) {
      last = e;
      if (!isTransientRpcError(e) || i === attempts - 1) throw e;
      await new Promise((r) => setTimeout(r, 700 * 2 ** i));
    }
  }
  throw last;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function tw(privateKey?: string): Promise<any> {
  const { TronWeb } = await import('tronweb');
  const headers = tronAuthHeaders();
  // Production TMA always has initData → proxy. Dev/browser without Telegram
  // falls back to public TronGrid so mock preview still builds txs.
  const fullHost = headers ? tronFullHost() : PUBLIC_FULL_HOST;
  const opts = privateKey
    ? { fullHost, privateKey, ...(headers ? { headers } : {}) }
    : { fullHost, ...(headers ? { headers } : {}) };
  return new TronWeb(opts);
}

export function tronAddressFromMnemonic(mnemonic: string, index = 0): string {
  return deriveTron(mnemonic, index).address;
}

/** One-time approve(spender, uint256.max) so the disperse contract can pull tokens.
 *  Energy must already be delegated (server rented it). Returns the txid. */
export async function sendTronApprove(mnemonic: string, index: number, spender: string): Promise<string> {
  const pk = tronPrivateKey(mnemonic, index);
  const client = await tw(pk);
  const contract = await client.contract(TRC20_APPROVAL_ABI, USDT_CONTRACT);
  const txid = await contract.approve(spender, MAX_UINT256).send({ feeLimit: 100_000_000, shouldPollResponse: false });
  return String(txid);
}

/** Current USDT allowance (base units) of `owner` for `spender`. */
export async function getTronAllowance(owner: string, spender: string): Promise<bigint> {
  const client = await tw();
  client.setAddress(owner);
  const contract = await client.contract(TRC20_APPROVAL_ABI, USDT_CONTRACT);
  const a = await contract.allowance(owner, spender).call();
  return BigInt(a.toString());
}

/** True when allowance is the near-max approval prepare requires (uint256/2).
 *  Covering only this transfer's amount is not enough: prepare then loops on
 *  approval_not_confirmed and never signs. Throws if the read fails. */
export async function tronHasMaxApproval(owner: string, spender: string): Promise<boolean> {
  return (await getTronAllowance(owner, spender)) >= BigInt(MAX_UINT256) / 2n;
}

/** Poll until `owner`'s allowance for `spender` covers `amounts`, else throw. The
 *  approve must be mined before disperse can transferFrom. */
export async function waitForTronAllowance(p: {
  owner: string;
  spender: string;
  amounts: string[];
  timeoutMs?: number;
  intervalMs?: number;
}): Promise<void> {
  const min = p.amounts.reduce((s, a) => s + BigInt(toUnits(a)), 0n);
  const deadline = Date.now() + (p.timeoutMs ?? 45_000);
  const intervalMs = p.intervalMs ?? 1_200;
  for (;;) {
    const a = await getTronAllowance(p.owner, p.spender).catch(() => 0n);
    if (a >= min) return;
    if (Date.now() >= deadline) throw new Error('approval_not_confirmed');
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export interface SignedTronTransaction {
  txID: string;
  raw_data: unknown;
  raw_data_hex: string;
  signature: string[];
  visible?: boolean;
}

/** Build + sign the atomic disperse tx WITHOUT broadcasting. The server stores
 *  and broadcasts it. recipients/amounts: [recipient, treasury] / [principal, fee]. */
export async function signTronDisperse(p: {
  mnemonic: string;
  index: number;
  contract: string;
  recipients: string[];
  amounts: string[];
}): Promise<{ txHash: string; signedTransaction: SignedTronTransaction }> {
  const pk = tronPrivateKey(p.mnemonic, p.index);
  const client = await tw(pk);
  const values = p.amounts.map((a) => toUnits(a));
  const owner = client.address.fromPrivateKey(pk);
  if (!owner) throw new Error('tron_owner_unavailable');
  const built = await withBuildRetry(() =>
    client.transactionBuilder.triggerSmartContract(
      p.contract,
      'disperseToken(address,address[],uint256[])',
      { feeLimit: 200_000_000, callValue: 0 },
      [
        { type: 'address', value: USDT_CONTRACT },
        { type: 'address[]', value: p.recipients },
        { type: 'uint256[]', value: values },
      ],
      client.address.toHex(owner),
    ),
  );
  if (!built.result?.result || !built.transaction?.txID) {
    throw new Error(`tron_disperse_build_failed:${JSON.stringify(built.result ?? {})}`);
  }
  // Extend expiration so the server's durable rebroadcast has time after a crash.
  const durable = await client.transactionBuilder.extendExpiration(built.transaction, 10 * 60);
  const signed = await client.trx.sign(durable, pk);
  if (typeof signed === 'string' || !signed.txID || !Array.isArray(signed.signature) || signed.signature.length === 0) {
    throw new Error('tron_disperse_sign_failed');
  }
  return { txHash: signed.txID, signedTransaction: signed as SignedTronTransaction };
}

/** Build + sign a NATIVE TRX transfer WITHOUT broadcasting (the server stores and
 *  broadcasts it). `amount` is a decimal TRX string; the sender pays its own
 *  bandwidth in TRX (no energy/sponsorship — this is the gas token itself). */
export async function signTronTransfer(p: {
  mnemonic: string;
  index: number;
  to: string;
  amount: string;
}): Promise<{ txHash: string; signedTransaction: SignedTronTransaction }> {
  const pk = tronPrivateKey(p.mnemonic, p.index);
  const client = await tw(pk);
  const owner = client.address.fromPrivateKey(pk);
  if (!owner) throw new Error('tron_owner_unavailable');
  const amountSun = Number(toUnits(p.amount, 6)); // 1 TRX = 1e6 sun
  const built = await withBuildRetry(() =>
    client.transactionBuilder.sendTrx(p.to, amountSun, client.address.toHex(owner)),
  );
  if (!built?.txID) throw new Error('tron_transfer_build_failed');
  const durable = await client.transactionBuilder.extendExpiration(built, 10 * 60);
  const signed = await client.trx.sign(durable, pk);
  if (typeof signed === 'string' || !signed.txID || !Array.isArray(signed.signature) || signed.signature.length === 0) {
    throw new Error('tron_transfer_sign_failed');
  }
  return { txHash: signed.txID, signedTransaction: signed as SignedTronTransaction };
}

/** Build + sign a plain USDT TRC-20 `transfer(to, amount)` WITHOUT broadcasting.
 *  Used for dual-leg sends (recipient + treasury fee) — OwnKey-style, no disperse. */
export async function signTronUsdtTransfer(p: {
  mnemonic: string;
  index: number;
  to: string;
  amount: string;
}): Promise<{ txHash: string; signedTransaction: SignedTronTransaction }> {
  const pk = tronPrivateKey(p.mnemonic, p.index);
  const client = await tw(pk);
  const owner = client.address.fromPrivateKey(pk);
  if (!owner) throw new Error('tron_owner_unavailable');
  const built = await withBuildRetry(() =>
    client.transactionBuilder.triggerSmartContract(
      USDT_CONTRACT,
      'transfer(address,uint256)',
      { feeLimit: 100_000_000, callValue: 0 },
      [
        { type: 'address', value: p.to },
        { type: 'uint256', value: toUnits(p.amount) },
      ],
      client.address.toHex(owner),
    ),
  );
  if (!built.result?.result || !built.transaction?.txID) {
    throw new Error(`tron_usdt_transfer_build_failed:${JSON.stringify(built.result ?? {})}`);
  }
  const durable = await client.transactionBuilder.extendExpiration(built.transaction, 10 * 60);
  const signed = await client.trx.sign(durable, pk);
  if (
    typeof signed === 'string' ||
    !signed.txID ||
    !Array.isArray(signed.signature) ||
    signed.signature.length === 0
  ) {
    throw new Error('tron_usdt_transfer_sign_failed');
  }
  return { txHash: signed.txID, signedTransaction: signed as SignedTronTransaction };
}
